import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import * as R from 'ramda';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getFormErrorsFlatNames } from 'CorePackage/MainModule/common/getFormErrorsFlatNames';
import { getNearestKnownFlatName } from 'CorePackage/MainModule/common/getNearestKnownFlatName';
import { getParentFlatName } from 'CorePackage/MainModule/common/getParentFlatName';

import { IDecisionFormRef } from '../interfaces/IDecisionFormRef';
import { IDecisionFormRenderProps } from '../components/DecisionForm';
import { IDecisionErrorFieldWithProps } from '../interfaces/IDecisionErrorFieldWithProps';
import { IDecisionForm } from '../interfaces/IDecisionForm';
import { decisionErrorFieldsArrangeArray } from '../constants/decisionErrorFieldsArrangeArray';
import { decisionFieldsDataByFlatNameMap } from '../constants/decisionFieldsDataByFlatNameMap';
import { getDecisionErrorFieldDataByFlatName } from '../common/getDecisionErrorFieldDataByFlatName';
import { decisionSetErrorFields } from '../actions/decisionActions';

interface IUseKnmFormErrorsInitParams {
  formRef: IDecisionFormRef;
  errors: IDecisionFormRenderProps['errors'];
  submitFailed: IDecisionFormRenderProps['submitFailed'];
  touched: IDecisionFormRenderProps['touched'];
  visited: IDecisionFormRenderProps['visited'];
}

export function useDecisionFormErrorsInit({
  formRef,
  errors,
  submitFailed,
  touched,
  visited,
}: IUseKnmFormErrorsInitParams) {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const setErrorFields = useCallback(
    (errorFields: IDecisionErrorFieldWithProps[]) => dispatch(decisionSetErrorFields(errorFields)),
    [dispatch],
  );

  const fieldsDataByFlatNameMap = useMemo(() => decisionFieldsDataByFlatNameMap(), []);

  const [visibleErrorsFieldsFlatNames, setVisibleErrorsFieldsFlatNames] = useState<Array<string>>([]);

  useEffect(() => {
    setVisibleErrorsFieldsFlatNames(prev => {
      // Ошибки зарегистрированных полей - как и раньше
      const registeredFieldsErrors: Array<string> = visited
        ? Object.keys(visited).filter((fieldName: keyof IDecisionForm) => {
            const fieldState = formRef.current.getFieldState(fieldName);

            // TODO: понять как исправить валидацию, когда прилетает объект

            return (
              (typeof fieldState?.error === 'string' || typeof fieldState?.error?.[0] === 'string') &&
              fieldState?.touched
            );
          })
        : [];

      /**
       * Ошибки полей внутри свёрнутых аккордеонов: KnmCollapse не рендерит children
       * до первого раскрытия, поэтому такие поля во final-form не зарегистрированы
       * и в `visited` не попадают - иконка ошибок в шапке не показывалась,
       * хотя у блока была красная рамка.
       */
      const collapsedBlocksErrors: Array<string> = submitFailed
        ? getFormErrorsFlatNames(errors)
            .map(flatName => getNearestKnownFlatName(flatName, fieldsDataByFlatNameMap))
            .filter((flatName): flatName is string => Boolean(flatName))
            .filter((fieldName: keyof IDecisionForm) => !formRef.current.getFieldState(fieldName)?.touched)
        : [];

      const newVisibleErrorsFieldsFlatNames = R.uniq([...registeredFieldsErrors, ...collapsedBlocksErrors]);

      if (prev.join() !== newVisibleErrorsFieldsFlatNames.join()) {
        return newVisibleErrorsFieldsFlatNames;
      }

      return prev;
    });
  }, [errors, submitFailed, touched, visited, formRef, fieldsDataByFlatNameMap]);

  useEffect(() => {
    const errorFieldsWithData = visibleErrorsFieldsFlatNames.map(errorFieldFlatName => ({
      ...getDecisionErrorFieldDataByFlatName(errorFieldFlatName, formRef),
      flatName: errorFieldFlatName,
    }));

    /**
     * Один контрол может писать сразу в несколько полей формы (зеркальные поля вроде
     * `...violations[i].title` у редактора описания), и валидатор ругается на каждое.
     * Одинаковый заголовок в пределах одного элемента списка - для пользователя одна
     * и та же строка, оставляем первую.
     */
    const errorFieldsDeduped = R.uniqBy(
      errorField => `${getParentFlatName(errorField.flatName)}|${errorField.title}`,
      errorFieldsWithData,
    );

    const errorFieldsWithDataSorted = errorFieldsDeduped.sort(
      (a, b) =>
        decisionErrorFieldsArrangeArray.indexOf(a.nameTemplate) -
        decisionErrorFieldsArrangeArray.indexOf(b.nameTemplate),
    );

    setErrorFields(errorFieldsWithDataSorted);
  }, [formRef, setErrorFields, visibleErrorsFieldsFlatNames]);
}
